import React, { useRef } from 'react';
import { Input, InputRef } from 'antd';

interface OTPInputProps {
  length: number;
  otpValues: string;
  setOtpValues: (value: string) => void;
  onEnter?: () => void;
}

const OTPInput: React.FC<OTPInputProps> = ({ length, otpValues, setOtpValues, onEnter }) => {
  const inputRefs = useRef<Array<InputRef | null>>(new Array(length).fill(null));

  const handleChange = (value: string, index: number) => {
    // Only accept digits
    if (!/^\d*$/.test(value)) return;
    
    const newOtpValues = otpValues.split('');
    newOtpValues[index] = value;
    setOtpValues(newOtpValues.join(''));

    // Move focus to the next input if not empty and it's not the last input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'Enter') {
      onEnter?.();
    }
  };

  // Handle paste event to fill in the OTP
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    // Filter out non-digits and limit to the OTP length
    const digits = pastedData.replace(/\D/g, '').slice(0, length);
    
    if (digits) {
      const newOtpValues = digits.padEnd(length, '').split('');
      setOtpValues(newOtpValues.join(''));
      
      // Focus on the appropriate field after paste
      if (digits.length < length) {
        inputRefs.current[digits.length]?.focus();
      } else {
        inputRefs.current[length - 1]?.focus();
      }
    }
  };

  return (
    <div className="flex gap-2 sm:gap-[8px] justify-center">
      {Array.from({ length }).map((_, index) => (
        <Input
          key={index}
          maxLength={1}
          placeholder="-"
          value={otpValues[index] || ''}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className="text-center font-medium text-xl sm:text-2xl w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center"
          style={{ lineHeight: '1', padding: '0' }}
        />
      ))}
    </div>
  );
};

export default OTPInput;
